import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Download, Film, Eye as EyeIcon, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { downloadCsv } from "@/lib/utils";
import { Modal, Field, TextArea, SelectField, AdminButton } from "@/pages/admin/ui";
import ImageUploader from "@/components/admin/ImageUploader";
import GalleryEditor from "@/components/admin/GalleryEditor";
import DaysEditor from "@/components/admin/DaysEditor";
import RouteEditor from "@/components/admin/RouteEditor";
import BrochureUploader from "@/components/admin/BrochureUploader";
import { INDIA_STATES, WORLD_COUNTRIES, FORMAT_LABEL, EXPERIENCE_CATEGORIES, locationLabel, fmtJourneyDate } from "@/lib/content";
import { WORLDS, EXPEDITION_TYPES, EXPEDITION_STATUS, OUTPUT_OPTIONS } from "@/lib/expeditions";

const JOURNEY_STATUSES = ["UPCOMING", "OPEN", "SOLD OUT", "COMPLETED"];
const toList = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const fromList = (a) => (a || []).join("\n");

const durationOf = (start, end) => {
  const s = new Date(start), e = new Date(end);
  if (isNaN(s) || isNaN(e)) return 1;
  return Math.max(Math.round((e - s) / 86400000) + 1, 1);
};
const formatOf = (d) => (d >= 8 ? "EXPEDITION" : d >= 4 ? "QUEST" : "TRAIL");

function JourneyForm({ f, setF, onSubmit, onCancel }) {
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const days = durationOf(f.start_date, f.end_date);
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <Field label="Title *" required value={f.title} onChange={set("title")} data-testid="jr-title" />
      <div className="grid grid-cols-2 gap-4">
        <SelectField label="Status" options={JOURNEY_STATUSES} value={f.status} onChange={set("status")} data-testid="jr-status" />
        <Field label="Order" type="number" value={f.order} onChange={set("order")} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Start date" type="date" value={f.start_date} onChange={set("start_date")} data-testid="jr-start" />
        <Field label="End date" type="date" value={f.end_date} onChange={set("end_date")} data-testid="jr-end" />
      </div>
      {f.start_date && f.end_date && (
        <p className="text-xs text-ptf-secondary -mt-2" data-testid="jr-format-hint">
          {days} {days === 1 ? "day" : "days"} → files under {FORMAT_LABEL[formatOf(days)]}s
        </p>
      )}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Price per seat (₹) *" type="number" required value={f.price} onChange={set("price")} data-testid="jr-price" />
        <Field label="Total seats *" type="number" required value={f.seats_total} onChange={set("seats_total")} data-testid="jr-seats" />
      </div>
      <Field label="Booking advance per seat (₹, optional. If set, online reservation charges this instead of the full price)" type="number" value={f.advance_amount} onChange={set("advance_amount")} data-testid="jr-advance" />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Location (free text)" value={f.location} onChange={set("location")} placeholder="e.g. Champawat" />
        <label className="block">
          <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Country</span>
          <select className="w-full border border-ptf-primary/20 px-3 py-2.5 text-sm outline-none focus:border-ptf-secondary transition-colors bg-white text-ptf-text mt-1" value={f.country || "India"} onChange={set("country")} data-testid="jr-country">
            {WORLD_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>
      {(f.country || "India") === "India" && (
        <label className="block">
          <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Region (State)</span>
          <select className="w-full border border-ptf-primary/20 px-3 py-2.5 text-sm outline-none focus:border-ptf-secondary transition-colors bg-white text-ptf-text mt-1" value={f.region} onChange={set("region")} data-testid="jr-region">
            <option value="">Select state</option>
            {INDIA_STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
      )}
      <Field label="Additional map regions (comma-separated state names, for expeditions spanning more than one state)" value={f.regions_s} onChange={set("regions_s")} placeholder="e.g. Lakshadweep, Andaman and Nicobar Islands" data-testid="jr-regions" />
      <div className="border border-ptf-primary/15 p-4 space-y-4">
        <p className="text-[0.65rem] uppercase tracking-[0.18em] text-ptf-primary/60">Expedition metadata (search, filters & map)</p>
        <div>
          <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Terrain (one or more)</span>
          <div className="flex flex-wrap gap-2 mt-2">
            {WORLDS.map((w) => (
              <button key={w.key} type="button" data-testid={`jr-terrain-${w.key.toLowerCase()}`}
                onClick={() => setF((p) => ({ ...p, terrains: (p.terrains || []).includes(w.key) ? p.terrains.filter((x) => x !== w.key) : [...(p.terrains || []), w.key] }))}
                className={`text-[0.65rem] uppercase tracking-[0.12em] px-3 py-1.5 border transition-colors ${(f.terrains || []).includes(w.key) ? "bg-ptf-primary text-white border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary"}`}>
                {w.name}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <SelectField label="Expedition type" options={["", ...EXPEDITION_TYPES]} value={f.expedition_type} onChange={set("expedition_type")} data-testid="jr-exp-type" />
          <SelectField label="Expedition status" options={Object.keys(EXPEDITION_STATUS)} value={f.expedition_status} onChange={set("expedition_status")} data-testid="jr-exp-status" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Latitude (map marker)" value={f.latitude} onChange={set("latitude")} placeholder="e.g. -2.333" data-testid="jr-lat" />
          <Field label="Longitude (map marker)" value={f.longitude} onChange={set("longitude")} placeholder="e.g. 34.833" data-testid="jr-lng" />
        </div>
        <TextArea label="Mission (shown on cards, search results and the expedition page)" value={f.mission} onChange={set("mission")} rows={2} data-testid="jr-mission" />
        <div>
          <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Outputs</span>
          <div className="flex flex-wrap gap-2 mt-2">
            {OUTPUT_OPTIONS.map((o) => (
              <button key={o} type="button" data-testid={`jr-output-${o.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setF((p) => ({ ...p, outputs: (p.outputs || []).includes(o) ? p.outputs.filter((x) => x !== o) : [...(p.outputs || []), o] }))}
                className={`text-[0.65rem] uppercase tracking-[0.12em] px-3 py-1.5 border transition-colors ${(f.outputs || []).includes(o) ? "bg-ptf-primary text-white border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary"}`}>
                {o}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="SEO title (optional)" value={f.seo_title} onChange={set("seo_title")} data-testid="jr-seo-title" />
          <Field label="Keywords (comma separated)" value={f.keywords} onChange={set("keywords")} placeholder="Tanzania wildlife expedition, documentary" data-testid="jr-keywords" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <SelectField label="Currency" value={f.currency || "INR"} onChange={set("currency")} options={["INR", "USD", "EUR"]} data-testid="jr-currency" />
          <Field label="Related story IDs (comma separated)" value={f.related_story_ids_s} onChange={set("related_story_ids_s")} data-testid="jr-related-stories" />
        </div>
        <TextArea label="SEO description (optional)" value={f.seo_description} onChange={set("seo_description")} rows={2} data-testid="jr-seo-desc" />
      </div>
      <div>
        <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Experience categories</span>
        <div className="flex flex-wrap gap-2 mt-2">
          {EXPERIENCE_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`jr-cat-${c.toLowerCase()}`}
              onClick={() => setF((p) => ({ ...p, categories: (p.categories || []).includes(c) ? p.categories.filter((x) => x !== c) : [...(p.categories || []), c] }))}
              className={`text-[0.65rem] uppercase tracking-[0.12em] px-3 py-1.5 border transition-colors ${(f.categories || []).includes(c) ? "bg-ptf-primary text-white border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      <TextArea label="Summary (card + intro line)" value={f.summary} onChange={set("summary")} data-testid="jr-summary" />
      <TextArea label="Description" value={f.description} onChange={set("description")} rows={5} />
      <TextArea label="Highlights (one per line)" value={f.highlights} onChange={set("highlights")} placeholder={"Track wildlife with professional photographers\nStay in community-run camps"} data-testid="jr-highlights" />
      <TextArea label="Activities (one per line)" value={f.activities} onChange={set("activities")} placeholder={"Wildlife photography\nGame drives\nVillage visits"} data-testid="jr-activities" />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Accommodation" value={f.accommodation} onChange={set("accommodation")} placeholder="e.g. Homestays and tented camps" data-testid="jr-accommodation" />
        <Field label="Meals" value={f.meals} onChange={set("meals")} placeholder="e.g. All meals, local cuisine" data-testid="jr-meals" />
      </div>
      <SelectField label="Activity level" options={["", "Light", "Light-Moderate", "Moderate", "Demanding", "Strenuous"]} value={f.activity_level} onChange={set("activity_level")} data-testid="jr-activity-level" />
      <DaysEditor value={f.days} onChange={(v) => setF((p) => ({ ...p, days: v }))} />
      <RouteEditor value={f.route} onChange={(v) => setF((p) => ({ ...p, route: v }))} />
      <TextArea label="Simple itinerary (fallback if no day-by-day, one step per line)" value={f.itinerary} onChange={set("itinerary")} placeholder={"Day 1: Arrive and settle into the homestay\nDay 2: Trek to the ridge"} data-testid="jr-itinerary" />
      <TextArea label="What's included (one per line)" value={f.includes} onChange={set("includes")} placeholder={"All meals with host families\nLocal guides and safety gear"} />
      <TextArea label="What's NOT included (one per line)" value={f.excludes} onChange={set("excludes")} placeholder={"Airfare to and from the start point\nTravel insurance\nPersonal expenses"} data-testid="jr-excludes" />
      <div className="border border-ptf-primary/15 p-4 space-y-4">
        <p className="text-[0.65rem] uppercase tracking-[0.18em] text-ptf-primary/60">Field experience narrative (shown as sections on the public page)</p>
        <TextArea label="Why this experience exists" value={f.purpose} onChange={set("purpose")} rows={3} data-testid="jr-purpose" />
        <TextArea label="The story" value={f.story} onChange={set("story")} rows={3} data-testid="jr-story" />
        <TextArea label="Conservation context (do not claim work the Foundation is not doing)" value={f.conservation_context} onChange={set("conservation_context")} rows={3} data-testid="jr-conservation" />
        <TextArea label="Photography & documentary" value={f.photography_note} onChange={set("photography_note")} rows={3} data-testid="jr-photography" />
        <TextArea label="Responsible exploration principles" value={f.responsible_note} onChange={set("responsible_note")} rows={3} data-testid="jr-responsible" />
        <TextArea label="Who this is for (one per line)" value={f.who_for} onChange={set("who_for")} placeholder={"Wildlife photographers\nDocumentary creators"} data-testid="jr-who-for" />
        <TextArea label="Related training themes (one per line, links to Learning & Training)" value={f.training_links} onChange={set("training_links")} placeholder={"Wildlife Photography\nField Documentation"} data-testid="jr-training-links" />
      </div>
      <ImageUploader multiple label="Cover images (first is the hero)" value={f.images} onChange={(v) => setF((p) => ({ ...p, images: v }))} />
      <GalleryEditor value={f.gallery} onChange={(v) => setF((p) => ({ ...p, gallery: v }))} />
      <BrochureUploader value={f.brochure_url} onChange={(v) => setF((p) => ({ ...p, brochure_url: v }))} />
      <div className="border border-ptf-primary/15 p-4 space-y-3">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={!!f.is_flagship} onChange={(e) => setF((p) => ({ ...p, is_flagship: e.target.checked }))} data-testid="jr-flagship" className="h-4 w-4 accent-ptf-secondary" />
          <span className="text-sm font-medium text-ptf-primary inline-flex items-center gap-1.5"><Film className="h-4 w-4" /> Flagship (documentary-led expedition)</span>
        </label>
        {f.is_flagship && (
          <TextArea label="The film (shown on the journey page)" value={f.flagship_note} onChange={set("flagship_note")} data-testid="jr-flagship-note" />
        )}
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <AdminButton variant="ghost" type="button" onClick={onCancel}>Cancel</AdminButton>
        <AdminButton type="submit" data-testid="save-journey">Save</AdminButton>
      </div>
    </form>
  );
}

function Bookings() {
  const qc = useQueryClient();
  const { data: bookings = [] } = useQuery({ queryKey: ["bookings"], queryFn: async () => (await api.get("/admin/bookings")).data });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/bookings/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["bookings"] }); toast.success("Booking deleted"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const exportCsv = () => downloadCsv(
    [["journey", "name", "email", "phone", "seats", "amount_inr", "status", "created_at"],
      ...bookings.map((b) => [b.journey_title, b.name, b.email, b.phone, b.seats, b.amount, b.status, b.created_at])],
    "journey-bookings.csv"
  );
  return (
    <div className="mt-14">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">Bookings ({bookings.length})</h2>
        {bookings.length > 0 && (
          <button onClick={exportCsv} data-testid="export-bookings-csv" className="inline-flex items-center gap-2 border border-ptf-primary/20 px-4 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ptf-primary hover:bg-ptf-primary/5 transition-colors">
            <Download className="h-3.5 w-3.5" /> Download CSV
          </button>
        )}
      </div>
      <div className="space-y-2">
        {bookings.length === 0 && <p className="text-sm text-ptf-text/50">No bookings yet.</p>}
        {bookings.map((b) => (
          <div key={b.id} data-testid="booking-row" className="border border-ptf-primary/10 bg-white p-5">
            <div className="flex flex-wrap justify-between gap-2">
              <p className="font-medium text-ptf-primary">{b.name} · {b.journey_title}</p>
              <span className="flex items-center gap-3">
                <span className={`text-[0.6rem] uppercase tracking-[0.12em] px-2 py-1 ${b.status === "paid" ? "bg-ptf-accent text-ptf-primary" : "bg-ptf-primary/10 text-ptf-primary"}`}>{b.status}</span>
                <span className="text-xs text-ptf-text/45">{new Date(b.created_at).toLocaleString("en-IN")}</span>
                <button onClick={() => { if (window.confirm("Delete this booking?")) del.mutate(b.id); }} data-testid="delete-booking" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
              </span>
            </div>
            <p className="text-sm text-ptf-secondary mt-1">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
            <p className="text-xs text-ptf-text/60 mt-1">{b.seats} seat(s) · ₹{Number(b.amount || 0).toLocaleString("en-IN")}</p>
            {b.message && <p className="text-sm text-ptf-text/75 mt-2">{b.message}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Reviews() {
  const qc = useQueryClient();
  const { data: reviews = [] } = useQuery({ queryKey: ["admin-reviews"], queryFn: async () => (await api.get("/admin/reviews")).data });
  const moderate = useMutation({
    mutationFn: ({ id, approved }) => api.put(`/admin/reviews/${id}`, { approved }),
    onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: ["admin-reviews"] }); toast.success(v.approved ? "Review approved" : "Review hidden"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/reviews/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-reviews"] }); toast.success("Review deleted"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  return (
    <div className="mt-14">
      <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary mb-5">Traveller Reviews ({reviews.length})</h2>
      <div className="space-y-2">
        {reviews.length === 0 && <p className="text-sm text-ptf-text/50">No reviews yet.</p>}
        {reviews.map((r) => (
          <div key={r.id} data-testid="admin-review-row" className="border border-ptf-primary/10 bg-white p-5">
            <div className="flex flex-wrap justify-between gap-2">
              <p className="font-medium text-ptf-primary">{r.name} · {r.journey_title} · {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</p>
              <span className="flex items-center gap-3">
                <span className={`text-[0.6rem] uppercase tracking-[0.12em] px-2 py-1 ${r.approved ? "bg-ptf-accent text-ptf-primary" : "bg-ptf-primary/10 text-ptf-primary"}`}>{r.approved ? "live" : "pending"}</span>
                <button
                  onClick={() => moderate.mutate({ id: r.id, approved: !r.approved })}
                  data-testid="review-moderate"
                  className="inline-flex items-center gap-1.5 border border-ptf-primary/20 px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-ptf-primary hover:bg-ptf-primary/5"
                >
                  {r.approved ? <><EyeOff className="h-3.5 w-3.5" /> Hide</> : <><EyeIcon className="h-3.5 w-3.5" /> Approve</>}
                </button>
                <button onClick={() => { if (window.confirm("Delete this review?")) del.mutate(r.id); }} data-testid="delete-review" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
              </span>
            </div>
            <p className="text-sm text-ptf-text/75 mt-2">{r.text}</p>
            {r.photos?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {r.photos.map((p) => (
                  <img key={p} src={p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`} alt="" className="h-16 w-20 object-cover border border-ptf-primary/10" />
                ))}
              </div>
            )}
            <p className="text-xs text-ptf-text/45 mt-2">{new Date(r.created_at).toLocaleString("en-IN")}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TravelAdmin() {
  const qc = useQueryClient();
  const { data: journeys = [] } = useQuery({ queryKey: ["journeys"], queryFn: async () => (await api.get("/journeys")).data });
  const create = useMutation({
    mutationFn: (body) => api.post("/admin/journeys", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["journeys"] }); toast.success("Journey created"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const update = useMutation({
    mutationFn: ({ id, body }) => api.put(`/admin/journeys/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["journeys"] }); toast.success("Journey updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/admin/journeys/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["journeys"] }); toast.success("Journey deleted"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { title: "", summary: "", description: "", location: "", country: "India", region: "", regions_s: "", start_date: "", end_date: "", price: 0, advance_amount: 0, seats_total: 0, activity_level: "", categories: [], purpose: "", story: "", conservation_context: "", photography_note: "", responsible_note: "", who_for: "", training_links: "", highlights: "", activities: "", accommodation: "", meals: "", days: [], route: [], itinerary: "", includes: "", excludes: "", images: [], gallery: [], is_flagship: false, flagship_note: "", brochure_url: "", status: "UPCOMING", order: 0, terrains: [], expedition_type: "", expedition_status: "UPCOMING", latitude: "", longitude: "", mission: "", outputs: [], seo_title: "", seo_description: "", keywords: "", currency: "INR", related_story_ids_s: "" };
  const [f, setF] = useState(empty);

  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (j) => {
    setEditing(j);
    setF({ ...j, country: j.country || "India", region: j.region || "", regions_s: (j.regions || []).join(", "), images: j.images || [], gallery: j.gallery || [], activity_level: j.activity_level || "", brochure_url: j.brochure_url || "", categories: j.categories || [], purpose: j.purpose || "", story: j.story || "", conservation_context: j.conservation_context || "", photography_note: j.photography_note || "", responsible_note: j.responsible_note || "", who_for: fromList(j.who_for), training_links: fromList(j.training_links), highlights: fromList(j.highlights), activities: fromList(j.activities), accommodation: j.accommodation || "", meals: j.meals || "", days: j.days || [], route: j.route || [], itinerary: fromList(j.itinerary), includes: fromList(j.includes), excludes: fromList(j.excludes), terrains: j.terrains || (j.world ? [j.world] : []), expedition_type: j.expedition_type || "", expedition_status: j.expedition_status || "UPCOMING", latitude: j.latitude ?? "", longitude: j.longitude ?? "", mission: j.mission || "", outputs: j.outputs || [], seo_title: j.seo_title || "", seo_description: j.seo_description || "", keywords: (j.keywords || []).join(", "), currency: j.currency || "INR", related_story_ids_s: (j.related_story_ids || []).join(", ") });
    setOpen(true);
  };
  const save = (e) => {
    e.preventDefault();
    const country = f.country || "India";
    const body = {
      ...f, country, region: country === "India" ? f.region : "",
      regions: (f.regions_s || "").split(",").map((s) => s.trim()).filter(Boolean),
      price: Number(f.price) || 0, advance_amount: Number(f.advance_amount) || 0, seats_total: Number(f.seats_total) || 0, order: Number(f.order) || 0,
      highlights: toList(f.highlights), activities: toList(f.activities),
      who_for: toList(f.who_for), training_links: toList(f.training_links), categories: f.categories || [],
      days: (f.days || []).filter((d) => (d.title || "").trim() || (d.description || "").trim()),
      route: (f.route || [])
        .filter((s) => (s.name || "").trim() && s.lat !== "" && s.lng !== "")
        .map((s) => ({ ...s, lat: Number(s.lat), lng: Number(s.lng), mode: s.mode || "road" })),
      itinerary: toList(f.itinerary), includes: toList(f.includes), excludes: toList(f.excludes), images: f.images, gallery: f.gallery,
      terrains: f.terrains || [], outputs: f.outputs || [],
      world: (f.terrains || [])[0] || f.world || "",
      latitude: f.latitude === "" || f.latitude === null ? null : Number(f.latitude),
      longitude: f.longitude === "" || f.longitude === null ? null : Number(f.longitude),
      keywords: (f.keywords || "").split(",").map((s) => s.trim()).filter(Boolean),
      currency: f.currency || "INR",
      related_story_ids: (f.related_story_ids_s || "").split(",").map((s) => s.trim()).filter(Boolean),
    };
    if (editing) update.mutate({ id: editing.id, body }, { onSuccess: () => setOpen(false) });
    else create.mutate(body, { onSuccess: () => setOpen(false) });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">Field Experiences</h2>
        <AdminButton onClick={openNew} data-testid="add-journey"><Plus className="h-4 w-4" /> Add</AdminButton>
      </div>
      <div className="space-y-2">
        {journeys.length === 0 && <p className="text-sm text-ptf-text/50">No field experiences yet. Add one to populate the Responsible Exploration page.</p>}
        {journeys.map((j) => (
          <div key={j.id} className="flex items-center justify-between gap-4 border border-ptf-primary/10 bg-white px-5 py-4">
            <div className="min-w-0">
              <p className="font-medium text-ptf-primary truncate">{j.title}{j.is_flagship ? " · ★ Flagship" : ""}</p>
              <p className="text-xs text-ptf-text/55 truncate mt-0.5">
                {j.duration_days > 0 ? `${FORMAT_LABEL[j.format]} · ${j.duration_days}d` : "Dates TBA"} · {j.status} · {j.price > 0 ? `₹${Number(j.price).toLocaleString("en-IN")}` : "Cost TBA"} · {j.seats_booked}/{j.seats_total} booked
                {j.start_date ? ` · ${fmtJourneyDate(j.start_date)}` : ""}{locationLabel(j) ? ` · ${locationLabel(j)}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openEdit(j)} data-testid={`edit-journey-${j.id}`} className="grid place-items-center h-9 w-9 border border-ptf-primary/15 hover:bg-ptf-primary/5"><Pencil className="h-4 w-4 text-ptf-secondary" /></button>
              <button onClick={() => { if (window.confirm("Delete this journey?")) remove.mutate(j.id); }} data-testid={`delete-journey-${j.id}`} className="grid place-items-center h-9 w-9 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-4 w-4 text-red-600" /></button>
            </div>
          </div>
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Journey" : "New Journey"}>
        <JourneyForm f={f} setF={setF} onSubmit={save} onCancel={() => setOpen(false)} />
      </Modal>
      <Bookings />
      <Reviews />
    </div>
  );
}
